import { ESearchFilters } from '@/constants/searchFilters';

import type { IAiSearchFilters } from './aiSearchToParams';

type TParams = Partial<Record<string, string | string[] | undefined>>;

function str(v: string | string[] | undefined): string | undefined {
  if (Array.isArray(v)) return v[0];
  return v ?? undefined;
}

function num(v: string | string[] | undefined): number | undefined {
  const s = str(v);
  if (s == null || s === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Inverse of aiSearchToParams: distills the renter's current `ESearchFilters`
 * URL params back into the extractor's IAiSearchFilters shape, so a refine
 * call ("make it pet friendly") can pass currentFilters and the extractor
 * can classify intent (narrow vs replace) instead of always starting fresh.
 *
 * Lossy by design — only the facets the extractor reasons about are
 * recovered (e.g. drive/tow `style` isn't reconstructed; the explicit
 * vehicleType classes carry the signal the extractor needs).
 */
export function paramsToAiSearch(params: TParams): IAiSearchFilters {
  const f: IAiSearchFilters = {};

  const address = str(params[ESearchFilters.ADDRESS]);
  if (address) f.address = address;

  const dateFrom = str(params[ESearchFilters.DATE_FROM]);
  if (dateFrom) f.dateFrom = dateFrom;
  const dateTo = str(params[ESearchFilters.DATE_TO]);
  if (dateTo) f.dateTo = dateTo;

  const adults = num(params[ESearchFilters.SLEEPS_ADULTS]);
  if (adults != null) f.sleepsAdults = adults;
  const children = num(params[ESearchFilters.SLEEPS_CHILDREN]);
  if (children != null) f.sleepsChildren = children;

  const seatbelts = num(params[ESearchFilters.SEATBELTS]);
  if (seatbelts != null) f.seatbelts = seatbelts;

  const type = str(params[ESearchFilters.FILTER_TYPE]);
  if (type) f.vehicleType = type.split(',').filter(Boolean);

  // price[min]/price[max] are in cents on the URL; the extractor speaks dollars.
  const priceMin = num(params[ESearchFilters.PRICE_MIN]);
  if (priceMin != null) f.priceMin = Math.round(priceMin / 100);
  const priceMax = num(params[ESearchFilters.PRICE_MAX]);
  if (priceMax != null) f.priceMax = Math.round(priceMax / 100);

  // length[between] is "0-25" — recover the upper bound.
  const length = str(params[ESearchFilters.LENGTH_BETWEEN]);
  if (length) {
    const upper = Number(length.split('-')[1]);
    if (Number.isFinite(upper)) f.lengthMax = upper;
  }

  const feature = str(params[ESearchFilters.FILTER_FEATURE]);
  if (feature) {
    const feats = feature.split(',').filter(Boolean);
    if (feats.includes('pet_friendly')) f.petFriendly = true;
    const rest = feats.filter(x => x !== 'pet_friendly');
    if (rest.length) f.features = rest;
  }

  if (str(params[ESearchFilters.DELIVERY_STATIONARY]) === 'stationary') f.deliveryStationary = true;
  else if (str(params[ESearchFilters.DELIVERY]) === 'true') f.delivery = true;

  if (str(params[ESearchFilters.INSTANT_BOOK]) === 'true') f.instantBook = true;
  if (str(params[ESearchFilters.UNLIMITED_MILES]) === 'true') f.unlimitedMiles = true;
  if (str(params[ESearchFilters.UNLIMITED_GENERATOR]) === 'true') f.unlimitedGenerator = true;

  const make = str(params[ESearchFilters.FILTER_VEHICLE_MAKE]);
  if (make) f.make = make.split(',').filter(Boolean);

  const model = str(params[ESearchFilters.FILTER_VEHICLE_MAKE_MODELS]);
  if (model) f.model = model.split(',').filter(Boolean);

  const yearMin = num(params[ESearchFilters.YEAR_GREATER]);
  if (yearMin != null) f.yearMin = yearMin;
  const yearMax = num(params[ESearchFilters.YEAR_LESSER]);
  if (yearMax != null) f.yearMax = yearMax;

  const score = num(params[ESearchFilters.SCORE]);
  if (score != null) f.minRating = score;

  // Host type: superhost / military-host ride on tags-slug; pro on ownerType.
  const tagsSlug = str(params[ESearchFilters.FILTER_TAGS_SLUG]);
  const hostType: string[] = [];
  if (tagsSlug) {
    const tags = tagsSlug.split(',');
    if (tags.includes('superhost')) hostType.push('superhost');
    if (tags.includes('military-host')) hostType.push('military');
  }
  if (str(params[ESearchFilters.FILTER_OWNERTYPE]) === 'dealer') hostType.push('pro');
  if (hostType.length) f.hostType = hostType;

  if (str(params[ESearchFilters.CANCEL_POLICY])?.split(',').includes('flexible')) {
    f.flexibleCancellation = true;
  }

  const towMax = num(params[ESearchFilters.WEIGHT_LESSER]);
  if (towMax != null) f.towWeightMax = towMax;

  const keywords = str(params[ESearchFilters.FILTER_KEYWORDS]);
  if (keywords) f.keywords = keywords;

  return f;
}
